/**
 * Promo Optimizer - Analytics-Powered Promotion Simulator
 * =================================================
 * Simulate promo impact using price elasticity models
 * Interactive discount slider with ROI calculator
 */

import { useState } from 'react';
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext';
import GlobalHeader from '../components/GlobalHeader';
import { DollarSign, TrendingUp, Target, Calculator, Sparkles, Loader2 } from 'lucide-react';

interface PromoImpact {
  sku: string;
  discountPercent: number;
  expectedSalesLift: number;
  expectedRevenue: number;
  expectedMargin: number;
  currentMargin: number;
  roi: number;
  recommendation: string;
}

export default function PromoOptimizer() {
  const { selectedFiscalWeek } = useFiscalWeekContext();
  const [selectedSKU, setSelectedSKU] = useState('SAMPLE-SKU-001');
  const [discountPercent, setDiscountPercent] = useState(15);
  const [promoImpact, setPromoImpact] = useState<PromoImpact | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const simulatePromo = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch('/api/promo-optimizer/simulate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sku: selectedSKU,
          discountPercent,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setPromoImpact(data.data);
      } else {
        setError(data.error || 'Simulation failed');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getROIColor = (roi: number) => {
    if (roi > 0.5) return 'text-green-600';
    if (roi > 0) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Promo Optimizer</h1>
              <p className="text-gray-600 mt-1">
                Analytics-powered promotion impact simulator using price elasticity models
              </p>
            </div>
          </div>
        </div>

        {/* Configuration Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4">Promotion Configuration</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* SKU Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select SKU
              </label>
              <input
                type="text"
                value={selectedSKU}
                onChange={(e) => setSelectedSKU(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Enter SKU"
              />
            </div>

            {/* Discount Slider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discount Percentage: {discountPercent}%
              </label>
              <input
                type="range"
                min="0"
                max="50"
                step="5"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
              </div>
            </div>
          </div>

          {/* Simulate Button */}
          <button
            onClick={simulatePromo}
            disabled={isLoading || !selectedSKU}
            className="w-full md:w-auto px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg font-medium hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Simulating...
              </>
            ) : (
              <>
                <Calculator className="w-5 h-5" />
                Simulate Promo Impact
              </>
            )}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 text-sm">{error}</p>
            <p className="text-red-600 text-xs mt-1">
              Note: Price elasticity models need to be trained on historical promo data first.
            </p>
          </div>
        )}

        {/* Results */}
        {promoImpact && (
          <div className="space-y-6">
            {/* Recommendation Card */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">Data-Driven Recommendation</h3>
                  <p className="text-gray-700">{promoImpact.recommendation}</p>
                </div>
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Sales Lift */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">Sales Lift</h4>
                </div>
                <div className="text-3xl font-bold text-blue-600">
                  +{promoImpact.expectedSalesLift.toFixed(1)}%
                </div>
                <p className="text-sm text-gray-600 mt-1">Expected increase in units sold</p>
              </div>

              {/* Expected Revenue */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-gray-900">Expected Revenue</h4>
                </div>
                <div className="text-3xl font-bold text-green-600">
                  ${promoImpact.expectedRevenue.toLocaleString()}
                </div>
                <p className="text-sm text-gray-600 mt-1">Projected total revenue</p>
              </div>

              {/* ROI */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-gray-900">ROI</h4>
                </div>
                <div className={`text-3xl font-bold ${getROIColor(promoImpact.roi)}`}>
                  {(promoImpact.roi * 100).toFixed(1)}%
                </div>
                <p className="text-sm text-gray-600 mt-1">Return on investment</p>
              </div>
            </div>

            {/* Margin Impact */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Margin Impact</h4>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Current Margin</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {(promoImpact.currentMargin * 100).toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Expected Margin</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {(promoImpact.expectedMargin * 100).toFixed(1)}%
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Margin Change:</span>
                  <span className={`font-semibold ${
                    promoImpact.expectedMargin < promoImpact.currentMargin
                      ? 'text-red-600'
                      : 'text-green-600'
                  }`}>
                    {((promoImpact.expectedMargin - promoImpact.currentMargin) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">How This Works</h4>
              <p className="text-sm text-blue-700 leading-relaxed">
                The Promo Optimizer uses statistical models trained on historical promotion data to estimate
                the impact of discounts on sales volume and margin. The analysis considers price elasticity,
                competitive dynamics, and seasonal patterns to provide ROI estimates.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
